import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './button';

/**
 * Usage:
 *   const [confirm, setConfirm] = useState(null);
 *
 *   // to trigger:
 *   setConfirm({ message: 'Delete this?', onConfirm: () => doDelete() });
 *
 *   // in JSX:
 *   <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
 */
export default function ConfirmDialog({ confirm, onClose }) {
  if (!confirm) return null;

  const handleConfirm = () => {
    confirm.onConfirm();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 animate-in fade-in zoom-in-95 duration-150">
        <div className="flex flex-col items-center text-center gap-3">
          <div className="h-12 w-12 rounded-full bg-red-50 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-red-500" />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-900 mb-1">
              {confirm.title || 'Are you sure?'}
            </h3>
            <p className="text-sm text-slate-500">{confirm.message}</p>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <Button
            variant="outline"
            className="flex-1 border-slate-200 text-slate-600 rounded-xl"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl"
            onClick={handleConfirm}
          >
            {confirm.confirmLabel || 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  );
}
